import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ensureSchemaType } from '@client/new/pages/Marketing/campaign-setup/useURLBuilderState';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormControls,
  muiForm,
  toFormControls,
} from '@client/new/helpers/forms';
import { useUpdate } from '@client/new/helpers/useUpdate';
import { Actions, F0, FSpread, TypedOmit } from '@client/new/helpers/types';
import {
  UpdatableVideo,
  UseVideosWriteModelParams,
  useVideosWriteModel,
} from '../models/videos.model';
import {
  NotificationsVM,
  useNotificationsModel,
} from '../../../../helpers/useNotifcations';
import { UploadMap, UploadState, Video } from '../models/library.domain';
import { pickObject } from '@client/new/helpers/general';
import { useMemo, useRef, useState } from 'react';
import { omit } from 'lodash';
import toast from 'react-hot-toast';
import { uploadFile } from '../models/upload.hooks';
import {
  useVideoStatusModel,
  VideoById,
  VideoStatus,
} from '@client/new/common/videos/useUpload';
import { trackEvent } from '@services/tracking';
import {
  ExerciseSuggestion,
  useExerciseSuggestionsModel,
} from '@client/new/pages/creator/library/models/exercise-suggestions.model';
import auth from '@client/components/auth';
import {
  GenerateDescriptionViewModel,
  useGenerateDescriptionVM,
} from '@client/new/pages/creator/library/view-models/generateDescription.viewModel';
import { useExerciseLibraryModel } from '@client/new/helpers/useExerciseLibrary';
import { Media } from '@client/new/pages/workout/models/workout.domain';

type PayloadMap = {
  selectThumbnail: number;
  restoreThumbnail: never;
  submitEdit: never;
  setEditingMode: EditVMState['editingMode'];
  applyGeneratedDescription: { name: string; description: string };
  exerciseTitleUpdate: {
    description: string;
    uuid: string;
  };
  applyHint: { hint: Hint; uuid: string };
  showReplaceConfirmation: never;
  closeConfirmation: never;
  requestClose: never;
  replaceUpload: File;
  cancelSingleUpload: never;
  clearReplaceFile: never;
};

export type VideoView = Pick<
  Video,
  | 'url'
  | 'thumbnailUrl'
  | 'duration'
  | 'width'
  | 'height'
  | 'downloadUrl'
  | 'name'
  | 'id'
> & {
  isMux: boolean;
};

const toVideoView = (v: Video): VideoView => ({
  ...pickObject(v, [
    'url',
    'thumbnailUrl',
    'duration',
    'width',
    'height',
    'downloadUrl',
    'name',
    'id',
  ]),
  isMux: v.url.includes('mux'),
});

type EditVMAction = Actions<PayloadMap>;
export type EditVMDispatcher = FSpread<PayloadMap>;

type EditableVideo = TypedOmit<UpdatableVideo, 'id' | 'videoSourceType'>;

const schema = ensureSchemaType<EditableVideo>()(
  z.object({
    name: z.string().nonempty('Field is required'),
    description: z.string().optional(),
    thumbnailTimestamp: z.number().optional(),
  }),
);

type EditVMState = {
  submitting: boolean;
  editingMode: 'Details' | 'Thumbnail';
  showReplaceConfirmation: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  replacingFile?: File;
};

export type EditVideoVM = EditVMState & {
  video: VideoView;
  isReplacing: boolean;
  uploadError: boolean;
  dispatch: EditVMDispatcher;
  form: FormControls<EditableVideo>;
  notifications: NotificationsVM;
  generateDescriptions: GenerateDescriptionViewModel;
  influencerId: number;
  tagLabel: 'Clip' | 'Video';
  inputHints: InputHints;
  uuid: string;
  uploadState: UploadState;
  exerciseSuggestions: ExerciseSuggestion[];
  canSeeExperimentalFeatures: boolean;
};

export type UseEditVideoVMParams = UseVideosWriteModelParams & {
  video: Video;
  folderId: number | null;
  onClose: F0;
  onEditSuccess: () => void;
  onReplaceSuccess: (video: VideoById) => void;
};

type Hint = { title?: string; description?: string };
type InputHints = Record<string, Hint>;

const uploadFileId = '1';
const cancelReplaceWarning =
  'Replacement is still in progress. Leaving now cancels it. The original video stays and the new file is discarded.';

export const useEditVideoVM = (p: UseEditVideoVMParams): EditVideoVM => {
  const writeModel = useVideosWriteModel({ influencerId: p.influencerId });
  const { pushNotification, ...notifications } = useNotificationsModel();
  const { addToLibrary } = useExerciseLibraryModel();
  const generateDescriptionVM = useGenerateDescriptionVM();

  const [inputHints, setInputHints] = useState<InputHints>({});
  const [uploadMap, setUploadMap] = useState<UploadMap>({});
  const [state, updateState] = useUpdate<EditVMState>({
    submitting: false,
    editingMode: 'Details',
    showReplaceConfirmation: false,
    isUploading: false,
    isProcessing: false,
    replacingFile: undefined,
  });

  const canSeeExperimentalFeatures = useMemo<boolean>(() => {
    const user = auth.getUser();
    return (
      user !== null &&
      (import.meta.env.VITE_EXPERIMENTAL_FEATURES_USERS || '')
        .split(',')
        .includes(user.email)
    );
  }, []);

  const replaceCancelled = useRef(false);
  const replacementId = useRef<string | null>(null);

  const { startPollingForVideo, stopPollingForVideo } = useVideoStatusModel({
    creatorId: p.influencerId,
  });

  const form = muiForm(
    useForm<EditableVideo, any, EditableVideo>({
      defaultValues: {
        name: p.video.name,
        description: p.video.description,
        thumbnailTimestamp: 0,
      },
      resolver: zodResolver(schema),
    }),
  );

  const { exerciseSuggestions } = useExerciseSuggestionsModel(
    form.watch('name'),
  );

  const isReplacing = state.isUploading || state.isProcessing;

  const dispatch: EditVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditVMAction;
    switch (action.type) {
      case 'requestClose':
        if (isReplacing) {
          if (!confirm(cancelReplaceWarning)) return null;
          cancelReplace();
        }
        p.onClose();
        return null;
      case 'restoreThumbnail':
        form.setValue('thumbnailTimestamp', undefined);
        return pushNotification('Custom', 'Thumbnail restored!');
      case 'selectThumbnail': {
        updateState;
        form.setValue('thumbnailTimestamp', action.payload);
        toast.success('Thumbnail changed!', { duration: 1000 });
        return null;
      }
      case 'submitEdit':
        onSubmitEdit();
        return null;
      case 'setEditingMode':
        return updateState({ editingMode: action.payload });
      case 'applyGeneratedDescription': {
        if (confirm('Add to Master Exercise Library?')) {
          addToLibrary(action.payload)
            .then(() =>
              toast.success(
                'Successfully added to the Master Exercise Library',
              ),
            )
            .catch(() =>
              toast.error(
                `Adding to the Master Exercise Library did not succeed`,
              ),
            );
        }

        form.setValue<'description'>('description', action.payload.description);
        return null;
      }
      case 'exerciseTitleUpdate': {
        const { description, uuid } = action.payload;
        const currentDescription = form.getValues('description');
        if (currentDescription) {
          setInputHints((prev) => ({
            ...prev,
            [uuid]: {
              ...(prev[uuid] || {}),
              description,
            },
          }));
          return null;
        }
        form.setValue('description', description);
        return null;
      }
      case 'applyHint': {
        const { hint, uuid } = action.payload;

        const updatedhints = omit(inputHints, [uuid]);
        setInputHints(updatedhints);
        if (hint.title) {
          form.setValue('name', hint.title);
        }
        if (hint.description) {
          form.setValue('description', hint.description);
        }
        return null;
      }
      case 'closeConfirmation':
        return updateState({ showReplaceConfirmation: false });
      case 'showReplaceConfirmation':
        return updateState({ showReplaceConfirmation: true });
      case 'replaceUpload':
        updateState({ replacingFile: action.payload });
        upload(action.payload);
        return null;
      case 'cancelSingleUpload':
        clearUploadMap();
        return updateState({ replacingFile: undefined, isUploading: false });
      case 'clearReplaceFile':
        clearUploadMap();
        return updateState({ replacingFile: undefined });
    }
  };

  const clearUploadMap = () => {
    if (uploadMap[uploadFileId]?.muxInstance) {
      uploadMap[uploadFileId].muxInstance.abort();
    }
    setUploadMap((prev) => {
      const next = { ...prev };
      delete next['1'];
      return next;
    });
  };

  const discardReplacement = (id: string) =>
    writeModel
      .deleteVideo({ id, videoSourceType: 'mux' })
      .catch((e) => console.error('Discarding replacement failed', e));

  const cancelReplace = () => {
    replaceCancelled.current = true;
    clearUploadMap();
    if (!replacementId.current) return;
    stopPollingForVideo(replacementId.current);
    discardReplacement(replacementId.current);
  };

  const onSubmitEdit = form.handleSubmit(async (payload) => {
    updateState({ submitting: true });
    try {
      await writeModel.updateVideo(
        {
          ...payload,
          id: p.video.id + '',
          type: p.video.type,
          videoSourceType: p.video.videoSourceType,
        },
        { ...form.formState.defaultValues },
      );
      p.onClose();
      p.onEditSuccess();
      pushNotification('Success', 'Video updated successfully!');
    } catch (e) {
      pushNotification('Error', 'Something went wrong!');
    } finally {
      updateState({ submitting: false });
    }
  });

  const markUploadFailed = () => {
    pushNotification('Error', 'Something went wrong!');
    setUploadMap((prev) => ({
      ...prev,
      [uploadFileId]: { ...prev[uploadFileId], state: 'error' },
    }));
  };

  const onProcessingFinished = (replacement: VideoById) => {
    if (replaceCancelled.current) return;
    if (!replacement || replacement.status === VideoStatus.ProcessingError) {
      markUploadFailed();
      return updateState({ isProcessing: false });
    }
    writeModel
      .deleteVideo({ id: p.video.id, videoSourceType: p.video.videoSourceType })
      .finally(() => {
        p.onReplaceSuccess(replacement);
        updateState({ isProcessing: false, replacingFile: undefined });
      });
  };

  const onUploadSuccess = (uploadedId: string) => {
    if (replaceCancelled.current) {
      discardReplacement(uploadedId);
      return;
    }
    replacementId.current = uploadedId;
    updateState({ isProcessing: true });
    startPollingForVideo(uploadedId, onProcessingFinished);
  };

  const upload = async (file: File) => {
    updateState({ isUploading: true });
    trackEvent({
      type: 'Video Upload Started',
      payload: { 'Number of Videos': 1 },
    });
    const updateFile = new File([file], p.video.name, {
      type: file.type,
    });
    uploadFile({
      video: {
        file: updateFile,
        id: uploadFileId,
      },
      influencerId: p.influencerId,
      setUploadMap,
      uploadMap,
      onSuccess: onUploadSuccess,
      folderId: p.folderId,
    })
      .catch(markUploadFailed)
      .finally(() => {
        updateState({ isUploading: false });
      });
  };

  const tagLabel = p.video.type === 'long' ? 'Video' : 'Clip';
  const uploadState = uploadMap[uploadFileId];

  return {
    ...state,
    video: toVideoView(p.video),
    isReplacing,
    uploadError: uploadState?.state === 'error',
    form: toFormControls(form),
    dispatch,
    notifications,
    generateDescriptions: generateDescriptionVM,
    influencerId: p.influencerId,
    tagLabel,
    inputHints,
    uuid: uploadFileId,
    uploadState,
    exerciseSuggestions,
    canSeeExperimentalFeatures,
  };
};
